const PREFIX = 'nad_cache:';
const DEFAULT_TTL_MS = 10 * 60 * 1000;

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const memory = new Map<string, CacheEntry<unknown>>();

function storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function readEntry<T>(key: string): CacheEntry<T> | null {
  const fullKey = PREFIX + key;
  const store = storage();
  if (store) {
    try {
      const raw = store.getItem(fullKey);
      if (raw) return JSON.parse(raw) as CacheEntry<T>;
    } catch {
      return null;
    }
  }
  const entry = memory.get(fullKey) as CacheEntry<T> | undefined;
  return entry ?? null;
}

export function getCached<T>(key: string): T | null {
  const entry = readEntry<T>(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    invalidate(key);
    return null;
  }
  return entry.value;
}

export function setCached<T>(key: string, value: T, ttlMs = DEFAULT_TTL_MS): void {
  const fullKey = PREFIX + key;
  const entry: CacheEntry<T> = { value, expiresAt: Date.now() + ttlMs };
  const store = storage();
  if (store) {
    try {
      store.setItem(fullKey, JSON.stringify(entry));
      return;
    } catch {
      // fall through to in-memory cache
    }
  }
  memory.set(fullKey, entry as CacheEntry<unknown>);
}

export function invalidate(key: string): void {
  const fullKey = PREFIX + key;
  const store = storage();
  if (store) {
    try {
      store.removeItem(fullKey);
    } catch {
      // ignore storage errors
    }
  }
  memory.delete(fullKey);
}

export function invalidatePrefix(prefix: string): void {
  const fullPrefix = PREFIX + prefix;
  const store = storage();
  if (store) {
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k && k.startsWith(fullPrefix)) keys.push(k);
      }
      keys.forEach((k) => store.removeItem(k));
    } catch {
      // ignore storage errors
    }
  }
  for (const k of Array.from(memory.keys())) {
    if (k.startsWith(fullPrefix)) memory.delete(k);
  }
}

export function clearCache(): void {
  invalidatePrefix('');
}

export const CACHE_TTL_MS = DEFAULT_TTL_MS;
